import * as React from 'react';
import { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  FormControl,
  Alert,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  TextField,
  Collapse,
  Checkbox,
  Tooltip,
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SendIcon from '@mui/icons-material/Send';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import TuneIcon from '@mui/icons-material/Tune';
import EmailIcon from '@mui/icons-material/Email';
import SmsIcon from '@mui/icons-material/Sms';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { withTranslation } from 'react-i18next';
import LanguageSelect from '../shared/LanguageSelect';
import axios from 'axios';
import { useUserStore, useSubscriptionStore } from '../../store';
import QuoteVersionList from './QuoteVersionList';

const DEFAULT_ADDITIONAL_TEXT = {
  fi: 'Lopullinen hinta voi muuttua työn edetessä, mikäli havaitaan lisäkorjaustarpeita.',
  en: 'The final price may change during the work if additional repairs are found necessary.',
  sv: 'Det slutliga priset kan ändras under arbetets gång om ytterligare reparationsbehov upptäcks.',
};

// PDF download names stay ASCII on purpose (no å/ä/ö in filenames).
const PDF_FILENAME_PREFIX = {
  kustannusarvio: { fi: 'Kustannusarvio_', en: 'Cost_Estimate_', sv: 'Kostnadsforslag_' },
  tarjous: { fi: 'Tarjous_', en: 'Quote_', sv: 'Offert_' },
};

// Organization-level text for the document language. An empty string is a deliberate
// "omit" (the setting says "Leave empty to omit"), so only a missing field (null, or a
// language the API does not send yet) falls back to the built-in default.
const orgAdditionalText = (org, lang) =>
  org?.[`offer_additional_text_${lang}`] ?? DEFAULT_ADDITIONAL_TEXT[lang] ?? DEFAULT_ADDITIONAL_TEXT.fi;

const formatCurrency = (amount) => {
  if (amount == null) return '-';
  return new Intl.NumberFormat('fi-FI', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount);
};

// What the document prints is remembered per browser, like the work order print options.
// Customer notes are on unless this browser turned them off (they are customer-visible
// anyway). Part codes and the booking time are off unless turned on, so a quote keeps its
// old look and a supplier code never reaches the customer by accident.
const PRINT_OPTIONS = {
  includeCustomerNotes: { key: 'offerIncludeCustomerNotes', fallback: true },
  showPartCodes: { key: 'offerShowPartCodes', fallback: false },
  includeSchedule: { key: 'offerIncludeSchedule', fallback: false },
};
const readPrintOption = (name) => {
  const { key, fallback } = PRINT_OPTIONS[name];
  try {
    const stored = localStorage.getItem(key);
    return stored === null ? fallback : stored === 'true';
  } catch {
    return fallback;
  }
};
const writePrintOption = (name, value) => {
  try {
    localStorage.setItem(PRINT_OPTIONS[name].key, value);
  } catch {
    // Private mode or blocked storage: the choice still applies to this dialog.
  }
};

// `allowSend` gates emailing the quote to the customer. The dialog itself is now
// reachable from any state (mechanics print quotes to check them), but mailing a
// "tarjous" for a job that is already completed or invoiced would confuse the
// customer and would overwrite last_offer_sent — so the caller keeps that to `quote`.
const OfferDialog = ({ open, handleClose, maintenance, onOfferSent, allowSend = true, t }) => {
  const [language, setLanguage] = useState(maintenance?.customer?.preferred_language || 'fi');
  // A finished job's document is a cost estimate after the fact, not an open offer.
  const [documentType, setDocumentType] = useState(
    maintenance?.state === 'quote' ? 'tarjous' : 'kustannusarvio'
  );
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [includeCustomerNotes, setIncludeCustomerNotes] = useState(() => readPrintOption('includeCustomerNotes'));
  const [showPartCodes, setShowPartCodes] = useState(() => readPrintOption('showPartCodes'));
  const [includeSchedule, setIncludeSchedule] = useState(() => readPrintOption('includeSchedule'));

  // Customization state
  const [showCustomize, setShowCustomize] = useState(false);
  const [validityDays, setValidityDays] = useState(14);
  const [additionalText, setAdditionalText] = useState('');
  const [orgDefaults, setOrgDefaults] = useState(null);

  // Delivery channels, acceptance request and the sent-version history
  const [sendEmail, setSendEmail] = useState(true);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [sendSms, setSendSms] = useState(false);
  const [phone, setPhone] = useState('');
  const [requestAcceptance, setRequestAcceptance] = useState(true);
  // The checkbox waits for the remembered choice, so a late answer never undoes a click.
  const [acceptanceLoaded, setAcceptanceLoaded] = useState(false);
  const [quotes, setQuotes] = useState([]);

  const currentOrganization = useUserStore((state) => state.currentOrganization);
  const { subscriptionInfo, fetchSubscriptionInfo, canUseSms } = useSubscriptionStore();
  const smsEnabled = canUseSms();
  const isTrial = subscriptionInfo?.is_trial;

  const fetchQuotes = useCallback(async () => {
    if (!maintenance?.id) return;
    try {
      const response = await axios.get(`/api/maintenances/${maintenance.id}/quotes/`);
      setQuotes(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setQuotes([]);
    }
  }, [maintenance?.id]);

  // The acceptance checkbox remembers the last sent tarjous (the caller's own, then the
  // workshop's); a workshop that has never sent one starts with it on.
  const fetchQuoteDefaults = useCallback(async () => {
    try {
      const response = await axios.get('/api/maintenances/quote-defaults/');
      setRequestAcceptance(response.data?.request_acceptance !== false);
    } catch (err) {
      setRequestAcceptance(true);
    } finally {
      setAcceptanceLoaded(true);
    }
  }, []);

  // Reset the delivery form from the customer's details every time the dialog opens
  useEffect(() => {
    if (open) {
      setRecipientEmail(maintenance?.customer?.email || '');
      setPhone(maintenance?.customer?.phone || '');
      setSendEmail(!!maintenance?.customer?.email);
      setSendSms(false);
      setRequestAcceptance(true);
      setAcceptanceLoaded(false);
      setIncludeCustomerNotes(readPrintOption('includeCustomerNotes'));
      setShowPartCodes(readPrintOption('showPartCodes'));
      setIncludeSchedule(readPrintOption('includeSchedule'));
      setError(null);
      fetchQuotes();
      fetchQuoteDefaults();
      if (!subscriptionInfo) {
        fetchSubscriptionInfo();
      }
    }
  }, [open, maintenance?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch org defaults on dialog open
  useEffect(() => {
    if (open && currentOrganization?.organization_id) {
      axios.get(`/api/organizations/${currentOrganization.organization_id}/`)
        .then((res) => {
          const org = res.data;
          setOrgDefaults(org);
          setValidityDays(org.offer_validity_days ?? 14);
          setAdditionalText(orgAdditionalText(org, language));
        })
        .catch(() => {
          // Use built-in defaults if fetch fails
          setValidityDays(14);
          setAdditionalText(DEFAULT_ADDITIONAL_TEXT[language] || DEFAULT_ADDITIONAL_TEXT.fi);
        });
    }
  }, [open, currentOrganization?.organization_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Switch additional text default when language changes
  const updateTextForLanguage = useCallback((lang) => {
    if (orgDefaults) {
      setAdditionalText(orgAdditionalText(orgDefaults, lang));
    } else {
      setAdditionalText(DEFAULT_ADDITIONAL_TEXT[lang] || DEFAULT_ADDITIONAL_TEXT.fi);
    }
  }, [orgDefaults]);

  const handleLanguageChange = (newLang) => {
    setLanguage(newLang);
    updateTextForLanguage(newLang);
  };

  // Calculate totals from maintenance (incl. VAT, matching maintenance page display)
  const calculateTotal = () => {
    let total = 0;
    if (maintenance?.parts) {
      total += maintenance.parts.reduce((sum, part) => sum + (parseFloat(part.line_total_incl_vat) || parseFloat(part.line_total) || 0), 0);
    }
    if (maintenance?.jobs) {
      total += maintenance.jobs.reduce((sum, job) => sum + (parseFloat(job.line_total_incl_vat) || parseFloat(job.line_total) || 0), 0);
    }
    return total;
  };

  const partsCount = maintenance?.parts?.length || 0;
  const jobsCount = maintenance?.jobs?.length || 0;
  const totalAmount = calculateTotal();
  const hasCustomer = !!maintenance?.customer;
  const hasContactDetails = !!(maintenance?.customer?.email || maintenance?.customer?.phone);
  const emailReady = sendEmail && recipientEmail.trim().length > 0;
  const smsReady = sendSms && phone.trim().length > 0;
  const canSend = emailReady || smsReady;
  const isQuote = documentType === 'tarjous';

  const getDownloadFilename = () => {
    const vehicleReg = maintenance?.vehicle?.registration_number || 'vehicle';
    const prefixes = PDF_FILENAME_PREFIX[documentType] || PDF_FILENAME_PREFIX.tarjous;
    return `${prefixes[language] || prefixes.fi}${vehicleReg}.pdf`;
  };

  const printOptionSetters = {
    includeCustomerNotes: setIncludeCustomerNotes,
    showPartCodes: setShowPartCodes,
    includeSchedule: setIncludeSchedule,
  };
  const handlePrintOptionChange = (name, checked) => {
    printOptionSetters[name](checked);
    writePrintOption(name, checked);
  };

  // Sent with preview, download and send alike, for both document types.
  const getPrintOptionParams = () => ({
    include_customer_notes: includeCustomerNotes,
    show_part_codes: showPartCodes,
    include_schedule: includeSchedule,
  });

  const getCustomizeParams = () => {
    if (documentType !== 'tarjous') return {};
    return {
      validity_days: validityDays,
      additional_text: additionalText,
    };
  };

  const handleDownload = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(`/api/maintenances/${maintenance.id}/offer_pdf/`, {
        params: {
          language,
          document_type: documentType,
          ...getPrintOptionParams(),
          ...getCustomizeParams(),
        },
        responseType: 'blob',
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', getDownloadFilename());
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.error || t('Failed to generate offer'));
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(`/api/maintenances/${maintenance.id}/offer_pdf/`, {
        params: {
          language,
          preview: 'true',
          document_type: documentType,
          ...getPrintOptionParams(),
          ...getCustomizeParams(),
        },
        responseType: 'blob',
      });

      // Open in new tab
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (err) {
      setError(err.response?.data?.error || t('Failed to generate offer'));
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    try {
      setSending(true);
      setError(null);

      const response = await axios.post(`/api/maintenances/${maintenance.id}/send_offer/`, {
        language,
        document_type: documentType,
        ...getPrintOptionParams(),
        ...getCustomizeParams(),
        send_email: emailReady,
        email: emailReady ? recipientEmail.trim() : undefined,
        send_sms: smsReady,
        phone: smsReady ? phone.trim() : undefined,
        request_acceptance: isQuote && requestAcceptance,
      });

      setSending(false);
      if (onOfferSent) {
        const recipient = [emailReady ? recipientEmail.trim() : null, smsReady ? phone.trim() : null]
          .filter(Boolean)
          .join(', ');
        onOfferSent(recipient, response.data);
      }
      handleCloseDialog();
    } catch (err) {
      setError(err.response?.data?.error || t('Failed to send offer'));
      setSending(false);
    }
  };

  const handleCloseDialog = () => {
    if (!loading && !sending) {
      setError(null);
      setShowCustomize(false);
      handleClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <DescriptionIcon color="primary" />
        {t('Create document')}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Summary */}
        <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2, mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t('Summary')}
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2">{t('Customer')}:</Typography>
            <Typography variant="body2" fontWeight={500}>
              {maintenance?.customer?.name || t('Not assigned')}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2">{t('Vehicle')}:</Typography>
            <Typography variant="body2" fontWeight={500}>
              {maintenance?.vehicle?.registration_number}
            </Typography>
          </Box>

          <Divider sx={{ my: 1.5 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2">{t('Parts')}:</Typography>
            <Typography variant="body2">{partsCount} {t('items')}</Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2">{t('Jobs')}:</Typography>
            <Typography variant="body2">{jobsCount} {t('items')}</Typography>
          </Box>

          <Divider sx={{ my: 1.5 }} />

          {parseFloat(maintenance?.order_discount_total || 0) > 0 && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" color="text.secondary">{t('Subtotal')}:</Typography>
                <Typography variant="body2">{formatCurrency(totalAmount)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" sx={{ color: 'success.main' }}>
                  {t('orderDiscount')}{parseFloat(maintenance?.discount_percent || 0) > 0 ? ` (${maintenance.discount_percent}%)` : ''}:
                </Typography>
                <Typography variant="body2" sx={{ color: 'success.main' }}>
                  −{formatCurrency(parseFloat(maintenance.order_discount_total))}
                </Typography>
              </Box>
            </>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body1" fontWeight={600}>{t('Total')}:</Typography>
            <Typography variant="body1" fontWeight={600} color="primary">
              {formatCurrency(maintenance?.total_with_vat ? parseFloat(maintenance.total_with_vat) : totalAmount)}
            </Typography>
          </Box>
        </Paper>

        {/* Document Type Selection */}
        <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
          <FormLabel component="legend">{t('Document type')}</FormLabel>
          <RadioGroup
            value={documentType}
            onChange={(e) => setDocumentType(e.target.value)}
          >
            <FormControlLabel
              value="tarjous"
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body2" fontWeight={500}>{t('Quote (Tarjous)')}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('Formal offer that the customer can accept or decline')}
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="kustannusarvio"
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body2" fontWeight={500}>{t('Cost estimate (Kustannusarvio)')}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('Non-binding estimate of costs')}
                  </Typography>
                </Box>
              }
            />
          </RadioGroup>
        </FormControl>

        {/* Language Selection */}
        <LanguageSelect
          fullWidth
          sx={{ mb: 3 }}
          value={language}
          label={t('Document language')}
          onChange={(e) => handleLanguageChange(e.target.value)}
        />

        {/* What the document prints (both tarjous and kustannusarvio) */}
        <Box sx={{ display: 'flex', flexDirection: 'column', mt: -1, mb: 2 }}>
          {[
            ['includeCustomerNotes', includeCustomerNotes, 'offer-include-customer-notes', t('Include customer notes')],
            ['showPartCodes', showPartCodes, 'offer-show-part-codes', t('Show part codes')],
            ['includeSchedule', includeSchedule, 'offer-include-schedule', t('Show booking time')],
          ].map(([name, checked, testId, label]) => (
            <FormControlLabel
              key={name}
              control={
                <Checkbox
                  checked={checked}
                  onChange={(e) => handlePrintOptionChange(name, e.target.checked)}
                  inputProps={{ 'data-testid': testId }}
                />
              }
              label={label}
            />
          ))}
        </Box>

        {/* Quote Customization (only for tarjous) */}
        {documentType === 'tarjous' && (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                mb: 1,
                gap: 0.5,
                '&:hover': { opacity: 0.8 },
              }}
              onClick={() => setShowCustomize(!showCustomize)}
            >
              <TuneIcon fontSize="small" color="action" />
              <Typography variant="subtitle2" color="text.secondary" sx={{ flex: 1 }}>
                {t('Customize quote')}
              </Typography>
              {showCustomize ? <ExpandLessIcon fontSize="small" color="action" /> : <ExpandMoreIcon fontSize="small" color="action" />}
            </Box>
            <Collapse in={showCustomize}>
              <Stack spacing={2} sx={{ mb: 3, pl: 1 }}>
                <TextField
                  label={t('Validity (days)')}
                  type="number"
                  value={validityDays}
                  onChange={(e) => setValidityDays(Math.max(0, parseInt(e.target.value) || 0))}
                  size="small"
                  helperText={validityDays === 0 ? t('Validity section will be hidden') : ''}
                  slotProps={{ input: { inputProps: { min: 0 } } }}
                  sx={{ maxWidth: 200 }}
                />
                <TextField
                  label={t('Additional text')}
                  value={additionalText}
                  onChange={(e) => setAdditionalText(e.target.value)}
                  size="small"
                  multiline
                  rows={2}
                  helperText={t('Leave empty to omit from document')}
                />
              </Stack>
            </Collapse>
          </>
        )}

        {/* Action Buttons */}
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
          {t('Actions')}
        </Typography>

        <Stack spacing={2}>
          <Button
            variant="outlined"
            fullWidth
            startIcon={loading ? <CircularProgress size={20} /> : <OpenInNewIcon />}
            onClick={handlePreview}
            disabled={loading || sending}
          >
            {t('Preview PDF')}
          </Button>

          <Button
            variant="outlined"
            fullWidth
            startIcon={loading ? <CircularProgress size={20} /> : <DownloadIcon />}
            onClick={handleDownload}
            disabled={loading || sending}
          >
            {t('Download PDF')}
          </Button>

          {allowSend && <Divider />}

          {!allowSend ? null : !hasCustomer ? (
            <Alert severity="info">
              {t('No customer assigned. Assign a customer with an email address to send the quote.')}
            </Alert>
          ) : (
            <Box data-testid="offer-send-block">
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                {t('quoteSnapshot.delivery')}
              </Typography>

              {!hasContactDetails && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t('quoteSnapshot.noContactDetails')}
                </Alert>
              )}

              <FormControlLabel
                control={<Checkbox checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmailIcon sx={{ fontSize: 20 }} />
                    <Typography variant="body2">{t('quoteSnapshot.sendByEmail')}</Typography>
                  </Box>
                }
              />
              {sendEmail && (
                <TextField
                  fullWidth
                  size="small"
                  type="email"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  placeholder={t('quoteSnapshot.recipientEmail')}
                  sx={{ mb: 1, ml: 4, width: 'calc(100% - 32px)' }}
                  inputProps={{ 'data-testid': 'offer-recipient-email' }}
                />
              )}

              <Tooltip title={!smsEnabled && isTrial ? t('SMS is not available during trial') : ''} placement="right">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={sendSms}
                      onChange={(e) => setSendSms(e.target.checked)}
                      disabled={!smsEnabled}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SmsIcon sx={{ fontSize: 20, color: smsEnabled ? 'inherit' : 'text.disabled' }} />
                      <Typography variant="body2" sx={{ color: smsEnabled ? 'inherit' : 'text.disabled' }}>
                        {t('quoteSnapshot.sendBySms')}
                      </Typography>
                      {!smsEnabled && isTrial && (
                        <InfoIcon sx={{ fontSize: 16, color: 'info.main', ml: 0.5 }} />
                      )}
                    </Box>
                  }
                />
              </Tooltip>
              {sendSms && (
                <TextField
                  fullWidth
                  size="small"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder={t('quoteSnapshot.phone')}
                  sx={{ mb: 1, ml: 4, width: 'calc(100% - 32px)' }}
                  inputProps={{ 'data-testid': 'offer-recipient-phone' }}
                />
              )}

              {isQuote && (
                <FormControlLabel
                  sx={{ mt: 1, alignItems: 'flex-start' }}
                  control={
                    <Checkbox
                      checked={requestAcceptance}
                      onChange={(e) => setRequestAcceptance(e.target.checked)}
                      disabled={!acceptanceLoaded}
                      sx={{ mt: -0.75 }}
                      inputProps={{ 'data-testid': 'offer-request-acceptance' }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={500} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CheckCircleOutlineIcon fontSize="small" color="success" />
                        {t('quoteSnapshot.requestAcceptance')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('quoteSnapshot.requestAcceptanceHelp')}
                      </Typography>
                    </Box>
                  }
                />
              )}

              <Button
                variant="contained"
                fullWidth
                sx={{ mt: 2 }}
                startIcon={sending ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                onClick={handleSend}
                disabled={loading || sending || !canSend}
                data-testid="offer-send-button"
              >
                {sending ? <span>{t('Sending...')}</span> : <span>{t('Send to customer')}</span>}
              </Button>
            </Box>
          )}
        </Stack>

        {/* Sent versions: every send is a frozen snapshot with its own PDF and answer */}
        <Divider sx={{ my: 3 }} />
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          {t('quoteSnapshot.sentQuotes')}
        </Typography>
        <QuoteVersionList quotes={quotes} dense onError={setError} data-testid="offer-history" />
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleCloseDialog} disabled={loading || sending}>
          {t('Close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default withTranslation()(OfferDialog);
